// Platform host resolution — the one place that knows which apex this build
// serves and how to split a hostname into "marketing apex" vs "tenant slug".
//
// Prod build: PLATFORM_HOST = stemne.no.
// QA build (Cloudflare Pages): set VITE_PLATFORM_HOST=qa.stemne.no so
// qa.stemne.no behaves as the marketing apex and {slug}.qa.stemne.no are
// tenants — a full mirror of prod. localhost is always treated as an apex
// for local dev.
//
// Everything that used to hardcode `=== 'stemne.no'` or count hostname labels
// (`parts.length >= 3`) should go through here; the label-counting heuristic
// was off-by-one for any multi-level apex like qa.stemne.no.

export const PLATFORM_HOST = (import.meta.env.VITE_PLATFORM_HOST as string) || 'stemne.no'

function currentHost(): string {
  return typeof window !== 'undefined' ? (window.location?.hostname ?? '') : ''
}

// Bases under which a leading label denotes a tenant slug. PLATFORM_HOST
// covers prod/QA; localhost covers `{slug}.localhost` in dev.
const tenantBases = [PLATFORM_HOST, 'localhost']

// isMarketingHost — the host is the platform apex (marketing root), not a
// tenant. Covers the apex, its www, and bare localhost.
export function isMarketingHost(host: string = currentHost()): boolean {
  return host === PLATFORM_HOST || host === `www.${PLATFORM_HOST}` || host === 'localhost'
}

// hostSlug — the tenant slug encoded in the hostname, or '' on the apex or an
// unrecognized host. Tenants are a single label below the base:
//   ada.stemne.no       (base stemne.no)    → ada
//   ada.qa.stemne.no    (base qa.stemne.no) → ada
//   ada.localhost       (dev)               → ada
export function hostSlug(host: string = currentHost()): string {
  if (!host || isMarketingHost(host)) return ''
  for (const base of tenantBases) {
    const suffix = `.${base}`
    if (host.endsWith(suffix)) {
      return host.slice(0, -suffix.length).split('.')[0] ?? ''
    }
  }
  return ''
}

// marketingOrigin — absolute URL of the marketing apex, for "back to the
// platform" links. Prod: https://stemne.no. QA: https://qa.stemne.no.
export function marketingOrigin(): string {
  return `https://${PLATFORM_HOST}`
}

// staffConsoleURL — absolute URL of the /staff console, for the
// dashboard's "Stemne staff" jump-link and the router's apex bounce
// on requiresStaff routes. The console lives at /staff on the
// platform apex; a tenant-subdomain visit must bounce there because
// the staff passkey ceremony is scoped to the apex (RP ID) and the
// session cookie with Domain=PLATFORM_HOST crosses subdomains.
//
// Dev keeps the current host because cookies set on {slug}.localhost
// don't propagate to bare localhost — a bounce would lose the JWT
// and the staff guard would kick the user home.
//
// Pre-fix this returned the literal 'https://stemne.no/staff' on
// every non-localhost host, which kicked QA staff into prod. The fix
// is to resolve via PLATFORM_HOST so QA → qa.stemne.no/staff.
export function staffConsoleURL(
  host: string = currentHost(),
  port: string = currentPort(),
): string {
  // Localhost dev: keep the current host so the host-only cookie
  // travels with the request. http://, since localhost dev runs plain.
  if (host.endsWith('localhost')) {
    const portSuffix = port ? `:${port}` : ''
    return `http://${host}${portSuffix}/staff`
  }
  // stemne.test mkcert dev (port present, non-localhost): same rule —
  // cookies are host-only (COOKIE_DOMAIN is empty per #825 and
  // /CLAUDE.md), so bouncing to the apex would strand the operator
  // without a JWT. The dev backend skips the staff sudo +
  // passkey-factor middlewares on /staff routes
  // (cmd/api/routes_platform.go) precisely so the console works from
  // whichever tenant subdomain the operator happens to be on.
  if (port) {
    return `https://${host}:${port}/staff`
  }
  // Prod / QA: no explicit port. Staff console lives at the platform
  // apex; the apex session cookie (Domain=PLATFORM_HOST) crosses
  // subdomains so a bounce from a tenant subdomain works cleanly.
  return `https://${PLATFORM_HOST}/staff`
}

// tenantOrigin — absolute URL of a tenant's apex, for the dashboard's
// "din side" share-card link. Empty slug yields '' so callers can
// conditionally render the link off when no slug is set.
//
// Pre-fix the dashboard composed `https://${slug}.stemne.no` inline,
// which gave QA tenants a link to the prod copy of their own
// subdomain. PLATFORM_HOST lookup keeps QA on qa.
export function tenantOrigin(slug: string, port: string = currentPort()): string {
  if (!slug) return ''
  // Carry the current port through when present so dev URLs don't
  // collapse to the implicit :443 (which Vite isn't listening on in
  // the *.stemne.test mkcert dev setup). Prod has no explicit port
  // so the suffix is empty there.
  const portSuffix = port ? `:${port}` : ''
  return `https://${slug}.${PLATFORM_HOST}${portSuffix}`
}

function currentPort(): string {
  return typeof window !== 'undefined' ? (window.location?.port ?? '') : ''
}

// effectivePlatformHost — runtime-correct apex for composing tenant
// URLs. In dev the build-time PLATFORM_HOST defaults to "stemne.no"
// (because VITE_PLATFORM_HOST is unset), but the actual host is
// `localhost:5173`. Composing `${slug}.${PLATFORM_HOST}` in dev
// produces "{slug}.stemne.no" which doesn't exist locally; the
// transfer-token return_to validation rejects, "Gå til dashboard"
// breaks, the share-card displays the wrong URL.
//
// Heuristic: if the current host endsWith 'localhost', we're in
// dev — return `localhost[:port]`. Otherwise use the build-time
// PLATFORM_HOST (correct in prod + QA, which both set the env var
// at build time so the constant matches runtime).
export function effectivePlatformHost(
  host: string = currentHost(),
  port: string = currentPort(),
): string {
  if (host.endsWith('localhost')) {
    return port ? `localhost:${port}` : 'localhost'
  }
  // Non-localhost apex — prod stemne.no (no port), QA qa.stemne.no
  // (no port), dev stemne.test (mkcert HTTPS on :5173). Carry the
  // port when present; suffix is empty under prod/QA so they don't
  // change shape.
  return port ? `${PLATFORM_HOST}:${port}` : PLATFORM_HOST
}

/** Builds the URL for a moved tenant: same page, first host label swapped
 *  for the tenant's current slug. Null when the swap is impossible (no
 *  subdomain to replace) or a no-op (already on the target slug) — the
 *  loop guard for a misbehaving backend. */
export function movedTenantUrl(
  loc: Pick<Location, 'protocol' | 'host' | 'pathname' | 'search' | 'hash'>,
  movedTo: string,
): string | null {
  if (!movedTo || !/^[a-z0-9-]+$/.test(movedTo)) return null
  const dot = loc.host.indexOf('.')
  if (dot <= 0) return null
  const currentLabel = loc.host.slice(0, dot)
  if (currentLabel === movedTo) return null
  return `${loc.protocol}//${movedTo}${loc.host.slice(dot)}${loc.pathname}${loc.search}${loc.hash}`
}
